import { createElement } from 'react';
import { z } from 'zod';

import {
    type DynamicWidgetAdapter,
    type DynamicWidgetBuildContext,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_registry';
import {
    buildBindingPresentation,
    buildDynamicWidgetId,
    type DynamicWidgetIssue,
    getDynamicWidgetSourceId,
    type OutreachHistoryWidgetModel,
    outreachHistoryWidgetModelSchema,
    type OutreachHistoryWidgetRow,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import {
    addRetrieveEnvelopeBindings,
    buildRetrieveArtifact,
    matchesRetrieveDomain,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/retrieve_artifact_adapter';
import { RetrieveArtifactController } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/retrieve_artifact_controller';
import { OutreachHistoryWidget } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/retrieve_outreach_history_widget';
import { type ToolExecutionEntry } from '@apps/web/components/phoebe_chat_view/transcript_types';

const DOMAIN = 'outreach_history';

const entitySchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
});

const shiftSchema = z.object({
    id: z.string().min(1),
    start_time: z.string().min(1),
    end_time: z.string().min(1),
});

const outreachHistorySourceRowSchema = z.object({
    outreach_id: z.string().min(1),
    outreach_type: z.string().min(1),
    attempt_id: z.string().nullable(),
    caregiver: entitySchema.nullable(),
    shift: shiftSchema.nullable(),
    channel: z.string().nullable(),
    outreach_created_at: z.string().min(1),
    outreach_updated_at: z.string().min(1),
    attempt_created_at: z.string().nullable(),
    attempt_updated_at: z.string().nullable(),
    response_at: z.string().nullable(),
    attempt_status: z.string().nullable(),
    status: z.string().min(1),
    outcome: z.string().nullable(),
});

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function optionalString(value: unknown): string | null {
    return typeof value === 'string' && value.length > 0 ? value : null;
}

function nestedRecord(
    value: unknown,
    field: string,
): Record<string, unknown> | null {
    if (!isRecord(value)) {
        return null;
    }
    const nested = value[field];
    return isRecord(nested) ? nested : null;
}

function mapOutreachHistoryRow(rawRow: unknown): OutreachHistoryWidgetRow {
    const parsed = outreachHistorySourceRowSchema.safeParse(rawRow);
    if (parsed.success) {
        return {
            outreachId: parsed.data.outreach_id,
            outreachType: parsed.data.outreach_type,
            attemptId: parsed.data.attempt_id,
            caregiverId: parsed.data.caregiver?.id ?? null,
            caregiverName: parsed.data.caregiver?.name ?? null,
            shiftId: parsed.data.shift?.id ?? null,
            shiftStartTime: parsed.data.shift?.start_time ?? null,
            shiftEndTime: parsed.data.shift?.end_time ?? null,
            channel: parsed.data.channel,
            outreachCreatedAt: parsed.data.outreach_created_at,
            responseAt: parsed.data.response_at,
            attemptStatus: parsed.data.attempt_status,
            status: parsed.data.status,
            outcome: parsed.data.outcome,
            valid: true,
        };
    }
    const row = isRecord(rawRow) ? rawRow : {};
    const caregiver = nestedRecord(row, 'caregiver');
    const shift = nestedRecord(row, 'shift');
    return {
        outreachId: optionalString(row['outreach_id']),
        outreachType: optionalString(row['outreach_type']),
        attemptId: optionalString(row['attempt_id']),
        caregiverId: optionalString(caregiver?.['id']),
        caregiverName: optionalString(caregiver?.['name']),
        shiftId: optionalString(shift?.['id']),
        shiftStartTime: optionalString(shift?.['start_time']),
        shiftEndTime: optionalString(shift?.['end_time']),
        channel: optionalString(row['channel']),
        outreachCreatedAt: optionalString(row['outreach_created_at']),
        responseAt: optionalString(row['response_at']),
        attemptStatus: optionalString(row['attempt_status']),
        status: optionalString(row['status']),
        outcome: optionalString(row['outcome']),
        valid: false,
    };
}

export function hydrateRetrieveOutreachHistoryWidgetModel(
    model: OutreachHistoryWidgetModel,
    rawRows: ReadonlyArray<unknown>,
    provenance: 'event_preview' | 'resource',
): OutreachHistoryWidgetModel {
    const sourceKind =
        provenance === 'event_preview' ? 'event_source' : 'resource_source';
    const source = model.sources.find(
        (candidate) => candidate.kind === sourceKind,
    );
    const sourceId =
        source === undefined ? null : getDynamicWidgetSourceId(source);
    const rows = rawRows.map(mapOutreachHistoryRow);
    const rowIssues: Array<DynamicWidgetIssue> = rows.flatMap(
        (row, rowIndex) =>
            row.valid
                ? []
                : [
                      {
                          id: `${model.widgetId}.history-row-${rowIndex}`,
                          code: 'source_invalid' as const,
                          level: 'warning' as const,
                          scope: 'section' as const,
                          bindingIds: [
                              provenance === 'event_preview'
                                  ? 'previewRows'
                                  : 'resourceRows',
                          ],
                          sourceIds: sourceId === null ? [] : [sourceId],
                          retryable: false,
                          safeDetails: { rowIndex },
                      },
                  ],
    );
    return outreachHistoryWidgetModelSchema.parse({
        ...model,
        rows,
        rowProvenance: provenance,
        issues: [
            ...model.issues.filter(
                (issue) =>
                    !issue.id.startsWith(`${model.widgetId}.history-row-`),
            ),
            ...rowIssues,
        ],
    });
}

export function buildRetrieveOutreachHistoryWidgetModel(
    entry: ToolExecutionEntry,
    context: DynamicWidgetBuildContext,
): OutreachHistoryWidgetModel | null {
    const artifact = buildRetrieveArtifact(entry, context, DOMAIN);
    if (artifact === null || !entry.toolCallEventId || !entry.runId) {
        return null;
    }
    addRetrieveEnvelopeBindings({ artifact });
    artifact.builder.addCalculatedField({
        id: 'preview.rows.fields',
        sourceIds: [artifact.eventSourceId],
        paths: [
            '/head_preview/*/outreach_id',
            '/head_preview/*/outreach_type',
            '/head_preview/*/attempt_id',
            '/head_preview/*/caregiver',
            '/head_preview/*/shift',
            '/head_preview/*/channel',
            '/head_preview/*/outreach_created_at',
            '/head_preview/*/response_at',
            '/head_preview/*/attempt_status',
            '/head_preview/*/status',
            '/head_preview/*/outcome',
        ],
        transform: 'mapRetrieveOutreachHistoryPreviewRows',
        rawValue: artifact.envelope.head_preview,
        value: null,
        renderedValue: null,
        presentation: buildBindingPresentation({
            section: 'rows',
            label: 'fields',
        }),
    });
    artifact.builder.addLiveField({
        id: 'resource.rows.fields',
        sourceIds: [artifact.resourceSourceId],
        paths: [
            '/rows/*/outreach_id',
            '/rows/*/outreach_type',
            '/rows/*/attempt_id',
            '/rows/*/caregiver',
            '/rows/*/shift',
            '/rows/*/channel',
            '/rows/*/outreach_created_at',
            '/rows/*/response_at',
            '/rows/*/attempt_status',
            '/rows/*/status',
            '/rows/*/outcome',
        ],
        presentation: buildBindingPresentation({
            section: 'rows',
            label: 'fields',
        }),
    });
    const receiptModel = outreachHistoryWidgetModelSchema.parse({
        ...artifact.builder.buildBase({
            widgetId: buildDynamicWidgetId(
                entry.toolCallEventId,
                'outreach_history',
            ),
            templateVersion: 1,
            issues: [],
        }),
        template: 'outreach_history',
        templateVersion: 1,
        rows: [],
        rowCount: artifact.envelope.row_count,
        rowProvenance: 'event_preview',
        organizationTimezone: context.organizationTimezone,
        workspaceFile: {
            runId: entry.runId,
            path: artifact.workspacePath,
        },
    });
    return hydrateRetrieveOutreachHistoryWidgetModel(
        receiptModel,
        artifact.envelope.head_preview,
        'event_preview',
    );
}

export const retrieveOutreachHistoryAdapter: DynamicWidgetAdapter<OutreachHistoryWidgetModel> =
    {
        template: 'outreach_history',
        templateVersion: 1,
        toolNames: new Set(['retrieve', 'retrieve_outreach_history']),
        matches: (entry) => matchesRetrieveDomain(entry, DOMAIN),
        build: buildRetrieveOutreachHistoryWidgetModel,
        render(model, handlers) {
            return createElement(
                RetrieveArtifactController<OutreachHistoryWidgetModel>,
                {
                    model,
                    hydrateRows: (previewModel, rows) =>
                        hydrateRetrieveOutreachHistoryWidgetModel(
                            previewModel,
                            rows,
                            'resource',
                        ),
                    render: ({
                        model: completeModel,
                        loadError,
                        hasMoreRows,
                        isLoadingMoreRows,
                        loadAllRows,
                    }) =>
                        createElement(OutreachHistoryWidget, {
                            model: completeModel,
                            timezone: model.organizationTimezone,
                            loadError,
                            hasMoreRows,
                            isLoadingMoreRows,
                            onShowAllRows: loadAllRows,
                            onCaregiverClick: handlers.onCaregiverClick,
                        }),
                },
            );
        },
    };
